// Connector slugs whose portal flow is a full OAuth handshake — a redirect to
// {b2cBase}/api/authorise/{slug} — rather than a name/credentials form. This is
// an explicit allow-list, deliberately NOT derived from the type's
// RequiresAuthorisation flag: several self-service connectors (Vend, Mailchimp,
// PayspaceDeelPayroll) carry RequiresAuthorisation=1 but are NOT added via this
// portal OAuth path, so keying off the flag would wrongly divert them here and
// break their create form. Add a slug only once its /api/authorise/{slug} route
// is confirmed working in prod.
//
// Single source of truth shared by the connector "add" flow
// (ConnectorCreateForm) and the "re-authorise" action (ConnectorCard) so the two
// can never drift.
export const OAUTH_SLUGS = new Set(["xero", "salesforce"]);

/**
 * Whether a connector type's authorisation is an OAuth handshake driven through
 * B2C. Case-insensitive on the slug.
 */
export function isOAuthConnector(typeSlug: string | undefined | null): boolean {
  return !!typeSlug && OAUTH_SLUGS.has(typeSlug.toLowerCase());
}

/**
 * Base URL of the B2C monolith that owns the OAuth authorise/callback routes.
 * Overridable per-environment via VITE_B2C_API_URL; falls back to prod.
 */
export function b2cApiBase(): string {
  return import.meta.env.VITE_B2C_API_URL || "https://business2cloud.larasoft.global";
}

/**
 * Builds the B2C authorise redirect URL used by BOTH the connector add-flow and
 * the re-authorise action.
 *
 * `state` is the Lara organisation identifier (the B2CClient key); B2C resolves
 * it back to the client in AuthoriseController.CallbackAsync. For a re-authorise
 * this is the existing connector's `organisationId` — the callback keys the
 * refreshed token by the provider's org id and upserts the existing client
 * connector in place, so re-authorising never creates a duplicate.
 *
 * `redirectOrigin` is the portal origin (window.location.origin); B2C validates
 * it against ALLOWED_PORTAL_URLS and ends on
 *   {redirectOrigin}/portal/connectors/{provider}/manage?organisation={state}#{key}
 */
export function buildConnectorAuthoriseUrl({
  typeSlug,
  state,
  redirectOrigin,
}: {
  typeSlug: string;
  state: string;
  redirectOrigin: string;
}): string {
  const redirectUri = `${redirectOrigin}/portal`;
  // Encode the slug path segment defensively. Both current callers gate on
  // isOAuthConnector (the {xero, salesforce} allow-list) so it can only be a
  // clean token today, but encoding keeps a future unguarded caller from
  // injecting into the path.
  const provider = encodeURIComponent(typeSlug.toLowerCase());
  return (
    `${b2cApiBase()}/api/authorise/${provider}` +
    `?state=${encodeURIComponent(state)}` +
    `&redirect_uri=${encodeURIComponent(redirectUri)}`
  );
}
